import _, { camelCase, isArray, uniqBy } from 'lodash';
import { Project, ProjectFieldValue } from 'api/project';
import { ResourceObject, StatusUpdate } from 'api/status-updates';
import { FieldValueType, Task } from 'api/tasks';
import { FilterableField } from 'components/organisms/tasks/fields';
import {
  ActiveDocumentInfo,
  DocumentResourceType,
  EditorModes,
  ResourceSection,
  ResourceSectionType,
} from 'data/documents';
import { TaskSectionMeta } from 'data/documents/saga/tasks/id-name-map';
import { getHeaderName } from 'routes/projects/list-v2/grid/get-header-name';
import { ProjectTimeTrackingFieldsName } from 'utils/project-fields/use-project-native-fields-as-filterable-fields';

const TranslationKeys = {
  arr_label: 'project.fields.arr',
  project_name_label: 'project.fields.project_name',
  project_fee_label: 'project.fields.project_fee',
  due_date_label: 'due_date',
  start_date_label: 'start_date',
  last_status_update_label: 'project.fields.last_status_update',

  progress_percent_label: 'project.fields.progress_percent',
  inferred_progress_label: 'project.fields.inferred_progress',
  project_age_label: 'project.fields.project_age',
  planned_duration_label: 'project.fields.planned_duration',
  actual_start_date_label: 'project.fields.actual_start_date',
  actual_completed_date_label: 'project.fields.actual_completed_date',

  trackedHoursLabel: 'project.fields.tracked_hours',
  budgetHoursLabel: 'project.fields.budgeted_hours',
  allocatedHoursLabel: 'project.fields.allocated_hours',
  customersInvitedLabel: 'project.fields.customers_invited',
  customersAcceptedLabel: 'project.fields.customers_accepted',
  projectCreatedOnLabel: 'project.fields.created_on',
  projectBudgetLabel: 'project.fields.project_budget',
  actualRevenueLabel: 'project.fields.actual_revenue',
  actualCostLabel: 'project.fields.actual_cost',
  actualProfitLabel: 'project.fields.actual_profit',
  actualProfitMarginLabel: 'project.fields.actual_profit_margin',

  estimateAtCompletionLabel: 'project.fields.estimate_at_completion',

  estimateToCompletionLabel: 'project.fields.estimate_to_completion',

  financialContractTypeLabel: 'project.fields.financial_contract_type',

  percentageBudgetedHoursConsumedLabel:
    'project.fields.percentage_budgeted_hours_consumed',

  percentageBudgetConsumedLabel: 'project.fields.percentage_budget_consumed',

  subscriptionFrequencyLabel: 'project.fields.subscription_frequency',

  numberOfPeriodsLabel: 'project.fields.number_of_periods',
};

export const FieldTypeFromBackend = {
  Boolean: 'BOOLEAN',
  Number: 'NUMBER',
  Text: 'TEXT',
  MultiLineText: 'MULTI_LINE_TEXT',
  Date: 'DATE',
  SingleSelect: 'SINGLE_SELECT',
  MultiSelect: 'MULTI_SELECT',
  SingleUser: 'SINGLE_USER',
  MultiUser: 'MULTI_USER',
  Assignee: 'ASSIGNEE',
  Estimate: 'ESTIMATE',
  Rating: 'RATING',
};

export type MinimalNativeFieldType = {
  fieldName: string;
  fieldLabel: string;
  type: string;
};

export const nativeFields = [
  {
    fieldName: 'projectName',
    fieldLabel: TranslationKeys.project_name_label,
    type: FieldTypeFromBackend.Text,
  },
  {
    fieldName: 'inferredProgress',
    fieldLabel: TranslationKeys.inferred_progress_label,
    type: FieldTypeFromBackend.SingleSelect,
    optionsAvailable: true,
  },
  {
    fieldName: 'dueDate',
    fieldLabel: TranslationKeys.due_date_label,
    type: FieldTypeFromBackend.Date,
  },
  {
    fieldName: 'startDate',
    fieldLabel: TranslationKeys.start_date_label,
    type: FieldTypeFromBackend.Date,
  },
  {
    fieldLabel: TranslationKeys.last_status_update_label,
    fieldName: 'lastStatusUpdate',
    type: FieldTypeFromBackend.Date,
  },
  {
    fieldLabel: TranslationKeys.progress_percent_label,
    fieldName: 'progressPercent',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.project_age_label,
    fieldName: 'projectAge',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.planned_duration_label,
    fieldName: 'plannedDuration',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.actual_start_date_label,
    fieldName: 'startDateActual',
    type: FieldTypeFromBackend.Date,
  },
  {
    fieldLabel: TranslationKeys.actual_completed_date_label,
    fieldName: 'dueDateActual',
    type: FieldTypeFromBackend.Date,
  },
  {
    fieldLabel: TranslationKeys.customersInvitedLabel,
    fieldName: 'customerMembersInvited',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.customersAcceptedLabel,
    fieldName: 'customerMembersAccepted',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.projectCreatedOnLabel,
    fieldName: 'createdAt',
    type: FieldTypeFromBackend.Date,
  },
  {
    fieldName: 'arr',
    fieldLabel: TranslationKeys.arr_label,
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldName: 'projectFee',
    fieldLabel: TranslationKeys.project_fee_label,
    type: FieldTypeFromBackend.Number,
  },

  {
    fieldLabel: TranslationKeys.projectBudgetLabel,
    fieldName: 'projectBudget',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.financialContractTypeLabel,
    fieldName: 'financialContractType',
    type: FieldTypeFromBackend.SingleSelect,
    optionsAvailable: true,
  },
  {
    fieldLabel: TranslationKeys.actualRevenueLabel,
    fieldName: 'actualRevenue',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.percentageBudgetConsumedLabel,
    fieldName: 'percentageBudgetConsumed',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.subscriptionFrequencyLabel,
    fieldName: 'subscriptionFrequency',
    type: FieldTypeFromBackend.SingleSelect,
    optionsAvailable: true,
  },
  {
    fieldLabel: TranslationKeys.numberOfPeriodsLabel,
    fieldName: 'numberOfPeriods',
    type: FieldTypeFromBackend.Number,
  },

  {
    fieldLabel: TranslationKeys.actualCostLabel,
    fieldName: 'actualCost',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.actualProfitLabel,
    fieldName: 'actualProfit',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.actualProfitMarginLabel,
    fieldName: 'actualProfitMargin',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.trackedHoursLabel,
    fieldName: ProjectTimeTrackingFieldsName.trackedHours,
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.budgetHoursLabel,
    fieldName: ProjectTimeTrackingFieldsName.budgetedMinutes,
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.allocatedHoursLabel,
    fieldName: ProjectTimeTrackingFieldsName.allocatedHours,
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.estimateAtCompletionLabel,
    fieldName: 'estimateAtCompletion',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.estimateToCompletionLabel,
    fieldName: 'estimateToComplete',
    type: FieldTypeFromBackend.Number,
  },
  {
    fieldLabel: TranslationKeys.percentageBudgetedHoursConsumedLabel,
    fieldName: 'percentageBudgetedHoursConsumed',
    type: FieldTypeFromBackend.Number,
  },
];

export function getFieldNamesFromIds(
  ids: number[] | string[],
  fields: FilterableField[],
): string[] {
  const fieldNames = ids.map((item) => {
    const id = Number(item);

    const foundField = fields.find((field) => field.fieldId === id);

    if (foundField) {
      if (foundField.customField) {
        return foundField?.fieldName
          ? camelCase(foundField?.fieldName ?? '')
          : '';
      }

      return foundField.fieldName;
    }

    return null;
  });

  return fieldNames.filter(Boolean) as string[];
}

export function getUserId(
  field: FilterableField,
  projectDetails: Project,
  customFieldData: ProjectFieldValue<FieldValueType> | undefined,
) {
  if (projectDetails[field.fieldName]) {
    return projectDetails[field.fieldName].userId;
  }

  return customFieldData?.fieldValue;
}
export function getUserIds(
  field: FilterableField,
  projectDetails: Project,
  customFieldData: ProjectFieldValue<FieldValueType> | undefined,
) {
  if (projectDetails[field.fieldName]) {
    if (isArray(projectDetails[field.fieldName])) {
      return projectDetails[field.fieldName]
        ?.map((user) => user?.userId)
        .filter(Boolean);
    }

    return projectDetails[field.fieldName];
  }
  if (customFieldData) {
    return customFieldData?.fieldValue;
  }

  return [];
}

export const filterableFieldType = (field) => {
  if (field.fieldName === 'assignee') {
    return FieldTypeFromBackend.Assignee;
  }

  if (field.fieldName === 'estimatedDueDate') {
    return FieldTypeFromBackend.Estimate;
  }

  if (field.fieldType === 'RATING') {
    return FieldTypeFromBackend.Rating;
  }

  switch (field.optionsAvailable ? field.fieldType : field.fieldDataType) {
    case 'BOOLEAN':
      return FieldTypeFromBackend.Boolean;
    case 'NUMBER':
      return FieldTypeFromBackend.Number;
    case 'TEXT':
      return FieldTypeFromBackend.Text;
    case 'MULTI_LINE_TEXT':
      return FieldTypeFromBackend.MultiLineText;
    case 'DATE':
      return FieldTypeFromBackend.Date;
    case 'USER':
      return field.fieldType === 'SINGLE_SELECT'
        ? FieldTypeFromBackend.SingleUser
        : FieldTypeFromBackend.MultiUser;
    case 'SINGLE_SELECT':
      return FieldTypeFromBackend.SingleSelect;
    case 'MULTI_SELECT':
      return FieldTypeFromBackend.MultiSelect;
    default:
      throw new Error('Unrecognized field type');
  }
};

export function getOrderedFields(
  resourceNames: string[],
  allProjectFields: FilterableField[] | ResourceObject[],
  extraFields: FilterableField[],
): FilterableField[] {
  const modifiedProjectFields = allProjectFields;

  const resourceToLoop =
    Array.isArray(resourceNames) && resourceNames && resourceNames?.length
      ? resourceNames
      : [];

  let sortedFields = resourceToLoop?.map((fieldName) => {
    const foundField = (
      modifiedProjectFields as unknown as FilterableField[]
    ).find(
      (field) =>
        field.fieldName === fieldName ||
        camelCase(field.fieldName) === fieldName ||
        field.fieldLabel === fieldName,
    );

    if (foundField) {
      return foundField;
    }

    return extraFields.find((field) => field.fieldName === fieldName);
  });

  sortedFields = sortedFields.map((field) =>
    field?.customField
      ? { ...field, fieldDataType: filterableFieldType(field) }
      : field,
  );

  return (sortedFields?.filter(Boolean) as FilterableField[]) || [];
}

export function getPayload(
  activeStatusUpdate: StatusUpdate | null,
  activeDocumentDetails: ActiveDocumentInfo,
  resourceSectionId: number,
  documentResources: (ResourceSection<Partial<Task>> | TaskSectionMeta)[],
  resourceNames: string[],
) {
  const payload = activeStatusUpdate
    ? {
        projectID: activeStatusUpdate?.project.projectId,
        documentID: activeStatusUpdate?.document.documentId,
        documentSectionId: activeDocumentDetails.publicSection.id,
        sectionType: EditorModes.Public,
        resourceSections: uniqBy(
          [
            {
              resourceSectionId: Number(resourceSectionId),
              resourceNames,
              resourceSectionType: ResourceSectionType.Summary,
              resourceType: DocumentResourceType.Default,
            },
            ...documentResources,
          ],
          'resourceSectionId',
        ),
      }
    : null;

  return payload;
}
export enum FilterFieldOptions {
  SELECTED = 'SELECTED',
  UNSELECTED = 'UNSELECTED',
}
export function getFilterdFields(
  fields: FilterableField[],
  searchText: string,
  fieldIds: number[],
  option: FilterFieldOptions,
) {
  const selectedFiltered = fields.filter((field) => {
    const foundField = fieldIds.find(
      (projectField) => projectField === field.fieldId,
    );

    if (
      foundField &&
      getHeaderName(field).toLowerCase().includes(searchText.toLowerCase())
    ) {
      return true;
    }

    return false;
  });
  const unSelectedFiltered = fields.filter((field) => {
    const foundField = fieldIds.find(
      (projectField) => projectField === field.fieldId,
    );

    if (
      !foundField &&
      getHeaderName(field).toLowerCase().includes(searchText.toLowerCase())
    ) {
      return true;
    }

    return false;
  });

  if (option === FilterFieldOptions.SELECTED) {
    return selectedFiltered;
  }

  return unSelectedFiltered;
}
